import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinalCta from "@/components/FinalCta";
import { API_URL } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const { openLead } = useSite();
  const [project, setProject] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setProject(null);
    setMissing(false);
    fetch(`${API_URL}/projects/${slug}`)
      .then((r) => {
        if (r.status === 404) {
          setMissing(true);
          return null;
        }
        return r.json();
      })
      .then((d) => d && setProject(d))
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <section className="section">
        <div className="wrap" style={{ textAlign: "center", padding: "60px 0" }}>
          <h1>Case study not available.</h1>
          <p className="muted" style={{ margin: "14px 0 24px" }}>It may be unpublished or the link may be old.</p>
          <Link className="btn" to="/projects">All projects <span>↗</span></Link>
        </div>
      </section>
    );
  }
  if (!project) return <div className="section"><div className="wrap"><p className="muted">Loading…</p></div></div>;

  const gen = project.annualGenerationMeasuredKwh
    ? { label: "Measured annual generation", value: `${Number(project.annualGenerationMeasuredKwh).toLocaleString()} kWh` }
    : project.annualGenerationEstKwh
      ? { label: "Estimated annual generation", value: `${Number(project.annualGenerationEstKwh).toLocaleString()} kWh (estimated — not measured)` }
      : null;
  return (
    <>
      <Seo title={`${project.title} | Solar Black Belt projects`} description={`Verified case study: ${project.title}${project.city ? `, ${project.city}` : ""}${project.state ? `, ${project.state}` : ""}. ${project.propertyType || ""} ${project.systemKw ? `${project.systemKw} kW solar` : ""}`.trim()} />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">VERIFIED CASE STUDY</span>
          <h1 data-testid="project-title">{project.title}</h1>
          <p className="lead">
            {[project.city, project.state].filter(Boolean).join(", ")}
            {project.propertyType ? ` · ${project.propertyType}` : ""}
            {project.completionDate ? ` · completed ${project.completionDate}` : ""}
          </p>
        </div>
      </section>
      <section style={{ paddingBottom: 40 }}>
        <div className="wrap split2">
          <Reveal>
            {project.photoKeys && project.photoKeys.length ? (
              <div style={{ display: "grid", gap: 12 }}>
                {project.photoKeys.map((k) => (
                  <img key={k} src={`${API_URL}/projects/photo/${k}`} alt={`${project.title} — approved project photograph`} style={{ width: "100%", borderRadius: 14, border: "1px solid var(--line)" }} loading="lazy" />
                ))}
              </div>
            ) : (
              <div className="panel"><p className="muted" style={{ fontSize: 13 }}>Approved project photographs are being added. Nothing is shown without authorization.</p></div>
            )}
          </Reveal>
          <Reveal delay={0.08}>
            <div className="panel" data-testid="project-facts">
              <span className="eyebrow">PROJECT FACTS</span>
              <table className="admin-table" style={{ marginTop: 12 }}>
                <tbody>
                  <tr><th>Scope & installation type</th><td>{project.scope || "Details available on request"}{project.installType ? ` · ${project.installType}` : ""}</td></tr>
                  <tr><th>System size</th><td>{project.systemKw ? `${project.systemKw} kW` : "Available on request"}</td></tr>
                  {project.batteryKwh ? <tr><th>Battery capacity</th><td>{project.batteryKwh} kWh</td></tr> : null}
                  {gen ? <tr><th>{gen.label}</th><td>{gen.value}</td></tr> : null}
                  {project.financialResults ? <tr><th>Documented financial results</th><td>{project.financialResults}</td></tr> : null}
                </tbody>
              </table>
              {project.testimonial && project.testimonialPermission ? (
                <blockquote style={{ margin: "18px 0 0", fontSize: 14, borderLeft: "3px solid var(--teal)", paddingLeft: 16 }}>
                  “{project.testimonial}”
                  <footer style={{ fontSize: 11, marginTop: 6 }} className="muted">Shared with customer permission.</footer>
                </blockquote>
              ) : null}
              <p className="micro">Published with the customer's permission. Measured and estimated figures are always labeled separately.</p>
              <button className="btn" style={{ marginTop: 16 }} onClick={() => openLead("commercial", `Similar project assessment — ${project.propertyType || "commercial"}`)} data-testid="project-cta">
                Discuss a project like this <span>↗</span>
              </button>
            </div>
          </Reveal>
        </div>
      </section>
      <FinalCta title={["Let's put your building", "to work."]} service="commercial" context="Quote request" />
    </>
  );
}
